import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import commonStore from "../Store/marco/commonStore";
import handleAPIError from "../services/toast";
import importJobStore, { parseImportDate } from "./import-job-store";
import { getImportDateLocale } from "./import-date-locale";
import {
  createImportContext,
  exportModuleExcel,
  IMPORT_MODULES,
  isWorkflowImportType,
} from "./import-job-config";
import { dataEntryText, getDataEntryImportLabel, hasSingleDataEntryCategory } from "./data-entry-excel";
import "./import-job.css";
import { OrganizationListExcelModal } from "../OrganizationalStructure/modals/organization-list-excel-modal";

const formatDate = (value) => {
  const date = parseImportDate(value);
  return date
    ? new Intl.DateTimeFormat(getImportDateLocale(commonStore.lang), {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date)
    : "-";
};

const VALIDATION_STAGES = [
  "queued",
  "parsing",
  "validating_structure",
  "validating_references",
  "done",
];

const getValidationStageLabel = (stage, t) => {
  const labels = {
    queued: "Preparing file",
    parsing: "Parsing file",
    validating_structure: "Validating file structure",
    validating_references: "Validating references",
    done: "Validation complete",
  };
  return t(labels[stage] || "Preparing file");
};

const ModulePill = ({ context }) => (
  <span
    className="import-job-module-pill"
    style={{ backgroundColor: context.moduleColor }}
  >
    {context.moduleAbbr}
  </span>
);

const Stats = ({ items }) => (
  <div className="import-job-stat-grid">
    {items.map((item) => (
      <div className="import-job-stat" key={item.label}>
        <strong style={{ color: item.color || "#1d2939" }}>{item.value}</strong>
        <span>{item.label}</span>
      </div>
    ))}
  </div>
);

const formatImportErrorField = (field, t) => {
  const match = /^YearMonth - (\d{4}-\d{2})$/.exec(field ?? "");
  return match ? `${t("YearMonth")} - ${match[1]}` : field || "-";
};

const formatImportErrorMessage = (message, t) => {
  const match = /^Data already exists for (\d{4}-\d{2})$/.exec(message ?? "");
  return match
    ? t("Data already exists for {{yearMonth}}", { yearMonth: match[1] })
    : message;
};

const JobErrorPreview = ({ errorCount, errors, t }) => {
  const previewErrors = errors.slice(0, 5);
  if (!previewErrors.length) return null;

  return (
    <Box sx={{ borderTop: "1px solid #eaecf0" }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ minHeight: 40, py: 0.75 }}
      >
        <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 700 }}>
          {t("First 5 errors")}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 11 }}>
          {t("Showing {{shown}} of {{total}} errors", {
            shown: previewErrors.length,
            total: errorCount,
          })}
        </Typography>
      </Stack>
      {previewErrors.map((error, index) => (
        <Box
          key={`${error.row}-${error.field}-${index}`}
          sx={{
            borderTop: "1px solid #f0f1f3",
            display: "grid",
            gap: 1.25,
            gridTemplateColumns: "76px minmax(0, 1fr)",
            py: 1,
          }}
        >
          <Typography sx={{ color: "#b42318", fontSize: 11, fontWeight: 600 }}>
            {error.row === 0 ? t("File") : `${t("Row")} ${error.row}`}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "#344054", fontSize: 11, fontWeight: 600, overflowWrap: "anywhere" }}>
              {formatImportErrorField(error.field, t)}
            </Typography>
            <Typography sx={{ color: "#667085", fontSize: 11, mt: 0.25 }}>
              {formatImportErrorMessage(error.message, t)}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const HistoryList = observer(({ t, onReupload }) => {
  const items = importJobStore.currentModuleHistory;

  if (importJobStore.historyLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 5, textAlign: "center" }}>
        {t("No import history")}
      </Typography>
    );
  }

  return (
    <div className="import-job-history-list">
      {items.map((item) => {
        const hasErrors = item.status === "completed" && item.errorRows > 0;
        const isFailed = item.status === "failed";
        const canReupload = hasErrors || isFailed;
        return (
          <div
            className="import-job-history-row"
            key={item.jobId}
            onClick={() => importJobStore.viewJob(item)}
          >
            {item.status === "processing" ? (
              <CircularProgress
                size={15}
                thickness={5}
                sx={{ color: "#1a56a8" }}
              />
            ) : (
              <span
                className="import-job-history-dot"
                style={{
                  background: canReupload ? "#c0392b" : "#1d7a4a",
                }}
              />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                {item.fileName}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 11 }}>
                {getDataEntryImportLabel(item.importType, t, commonStore.lang)}{" "}
                {isFailed ? t("Failed") : t("Completed")} |{" "}
                {formatDate(item.createdAt)} | {item.successRows} {t("imported")} |{" "}
                {item.errorRows} {t("errors")}
              </Typography>
            </Box>
            {canReupload && (
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={(event) => {
                  event.stopPropagation();
                  onReupload(item);
                }}
              >
                {t("Re-upload")}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
});

const WorkflowImportJobModal = observer(() => {
  const { t } = useTranslation("ActivityMain");
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [exportAll, setExportAll] = useState(false);
  const [isSummary, setIsSummary] = useState(false);

  const pendingItem = importJobStore.pendingOpenItem?.item;
  const importType = pendingItem?.importType || commonStore.excelImportType;
  const open =
    commonStore.showExcelImportExportModal &&
    isWorkflowImportType(importType);
  const context = createImportContext(pendingItem || { importType });

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setExportAll(false);
    setIsSummary(false);
    importJobStore.openModal(
      createImportContext(
        importJobStore.pendingOpenItem?.item || { importType },
      ),
    );
  }, [open, importType]);

  const closeModal = () => {
    if (importJobStore.isValidating) {
      importJobStore.notifyValidationMustRemainOpen();
      return;
    }
    commonStore._handleSetIsShowExcelImportExportModal(false);
    setFile(null);
  };

  const handleDialogClose = (_event, _reason) => closeModal();

  const isDataEntry = context.module === IMPORT_MODULES.DE;
  const missingCategory = isDataEntry && !hasSingleDataEntryCategory(context.selectedTab, context.standardId);
  const deText = (key) => dataEntryText(key, t, commonStore.lang);

  const handleExport = async () => {
    if (missingCategory) return;
    importJobStore.setExporting(true);
    try {
      await exportModuleExcel(context, exportAll, isSummary);
    } catch (error) {
      handleAPIError(error, {
        i18nKey: "Unable to export Excel file",
        ns: "ActivityMain",
        fallback: "Unable to export Excel file",
        throwError: false,
      });
    } finally {
      importJobStore.setExporting(false);
    }
  };

  const resetForUpload = () => {
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
    importJobStore.resetUpload();
  };

  const handleReupload = () => resetForUpload();

  const renderIdle = () => {
    const missingYear =
      [IMPORT_MODULES.EA, IMPORT_MODULES.DE].includes(context.module) &&
      !context.selectedYear;

    return (
      <Stack spacing={2}>
        {missingYear && (
          <Alert severity="warning">
            {t("Select a reporting year before importing a file.")}
          </Alert>
        )}
        <Typography color="text.secondary" sx={{ fontSize: 13 }}>
          {t("New file upload")}
        </Typography>
        <input
          accept=".xlsx"
          hidden
          ref={inputRef}
          type="file"
          onChange={(event) => setFile(event.target.files?.[0] || null)}
        />
        <div
          className={`import-job-file-zone ${missingYear ? "is-disabled" : ""}`}
          onClick={() => !missingYear && inputRef.current?.click()}
          role="button"
          tabIndex={missingYear ? -1 : 0}
          onKeyDown={(event) => {
            if (!missingYear && (event.key === "Enter" || event.key === " ")) {
              inputRef.current?.click();
            }
          }}
        >
          <UploadFileIcon sx={{ color: "#667085", fontSize: 30 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {file?.name || t("Choose a .xlsx file to import")}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 11 }}>
            {t("Only .xlsx files are accepted")}
          </Typography>
        </div>
      </Stack>
    );
  };

  const renderValidating = () => {
    const validation = importJobStore.validation || {};
    const currentStageIndex = Math.max(
      0,
      VALIDATION_STAGES.indexOf(validation.stage),
    );
    const visibleStages = VALIDATION_STAGES.slice(0, currentStageIndex + 1);
    const validationDone = validation.stage === "done";

    return (
      <Stack spacing={1.75}>
        <Typography
          color="text.secondary"
          sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}
        >
          {t("Checking file...")}
        </Typography>
        <Box
          sx={{
            alignItems: "center",
            border: "1px solid #d0d5dd",
            borderRadius: "6px",
            display: "flex",
            gap: 1,
            minHeight: 42,
            px: 1.5,
          }}
        >
          <UploadFileIcon sx={{ color: "#667085", fontSize: 20 }} />
          <Typography noWrap sx={{ color: "#344054", fontSize: 13, fontWeight: 600 }}>
            {validation.fileName || file?.name}
          </Typography>
        </Box>
        <Stack spacing={0.5}>
          {visibleStages.map((stage, index) => {
            const isComplete = validationDone || index < currentStageIndex;
            return (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ minHeight: 28 }}
                key={stage}
              >
                {isComplete ? (
                  <CheckCircleIcon sx={{ color: "#1d7a4a", fontSize: 17 }} />
                ) : (
                  <CircularProgress
                    size={16}
                    thickness={5}
                    sx={{ color: "#1a73c9" }}
                  />
                )}
                <Typography
                  sx={{
                    color: isComplete ? "#667085" : "#344054",
                    fontSize: 12,
                    fontWeight: isComplete ? 400 : 600,
                  }}
                >
                  {getValidationStageLabel(stage, t)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        <Alert severity="info" sx={{ mt: 0.5 }}>
          {t("File validation is in progress. Keep this window open.")}
        </Alert>
      </Stack>
    );
  };

  const renderValidationError = () => {
    const validation = importJobStore.validation || {};
    const errors = Array.isArray(validation.errors) ? validation.errors : [];
    return (
      <Stack spacing={2}>
        <div className="import-job-error-panel">
          <Stack direction="row" spacing={1} alignItems="center">
            <ErrorOutlineIcon />
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {t("File cannot be imported")}
              </Typography>
              <Typography sx={{ fontSize: 11 }}>
                {validation.errorCount || errors.length} {t("errors found")}
              </Typography>
            </Box>
          </Stack>
        </div>
        {errors.length > 0 && (
          <Box sx={{ maxHeight: 240, overflow: "auto" }}>
            {errors.map((error, index) => (
              <Box
                key={`${error.row}-${error.field}-${index}`}
                sx={{
                  borderBottom: "1px solid #eceef1",
                  display: "grid",
                  gridTemplateColumns: { xs: "54px minmax(0, 1fr)", sm: "60px minmax(0, 1fr) minmax(0, 1fr)" },
                  overflowWrap: "anywhere",
                  gap: 1,
                  py: 1,
                }}
              >
                <Typography sx={{ color: "#c0392b", fontSize: 11 }}>
                  {error.row === 0 ? t("File") : `${t("Row")} ${error.row}`}
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                  {formatImportErrorField(error.field, t)}
                </Typography>
                <Typography sx={{ fontSize: 11 }}>
                  {formatImportErrorMessage(error.message, t)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        <Typography color="text.secondary" sx={{ fontSize: 11 }}>
          {t("No data has been imported. This validation is not recorded in import history.")}
        </Typography>
      </Stack>
    );
  };

  const renderLocked = () => {
    const job = importJobStore.activeJob;
    return (
      <div className="import-job-lock-panel">
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <LockOutlinedIcon />
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {t("Import is currently locked")}
            </Typography>
            <Typography sx={{ fontSize: 12, mt: 0.5 }}>
              {t("An import is in progress. All EA, DE and EF modules are locked until it completes.")}
            </Typography>
            {job && (
              <Typography sx={{ fontSize: 11, mt: 1 }}>
                {t(getModuleMetaLabel(job.module))} | {job.fileName} | {job.progress}%
              </Typography>
            )}
          </Box>
        </Stack>
      </div>
    );
  };

  const renderProcessing = () => {
    const job = importJobStore.selectedJob || importJobStore.activeJob;
    return (
      <Stack spacing={2}>
        <div className="import-job-status-panel">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={22} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                {job?.fileName || t("Import in progress")}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 11 }}>
                {job?.processedRows || 0} / {job?.totalRows || 0} {t("rows processed")}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              {job?.progress || 0}%
            </Typography>
          </Stack>
          <LinearProgress
            sx={{ height: 6, mt: 1.5, borderRadius: 3 }}
            variant="determinate"
            value={job?.progress || 0}
          />
        </div>
        <Alert severity="warning">
          {t("All EA, DE and EF modules are locked while this import runs. You can close this dialog and monitor progress from the bell.")}
        </Alert>
      </Stack>
    );
  };

  const renderResult = () => {
    const job = importJobStore.selectedJob;
    const hasErrors = job?.errorRows > 0;
    const isFailed = job?.status === "failed";
    const isErrorResult = hasErrors || isFailed;
    const summary = job?.summary || {};
    const errors = Array.isArray(job?.errors) ? job.errors : [];
    const errorCount = job?.errorCount ?? job?.errorRows ?? errors.length;
    return (
      <Stack spacing={2}>
        <div
          className={
            isErrorResult
              ? "import-job-error-panel"
              : "import-job-success-panel"
          }
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {isErrorResult ? <ErrorOutlineIcon /> : <CheckCircleOutlineIcon />}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {isFailed
                  ? t("Import failed")
                  : hasErrors
                  ? t("Import completed with errors")
                  : t("Import completed successfully")}
              </Typography>
              <Typography noWrap sx={{ fontSize: 11 }}>
                {job?.fileName} | {formatDate(job?.completedAt || job?.createdAt)}
              </Typography>
            </Box>
          </Stack>
        </div>
        <Stats
          items={[
            {
              label: t("Imported"),
              value: summary.imported ?? job?.successRows ?? 0,
              color: "#1d7a4a",
            },
            {
              label: t("Errors"),
              value: summary.errors ?? job?.errorRows ?? 0,
              color: "#c0392b",
            },
            {
              label: t("Processed"),
              value:
                summary.actionableRows ??
                job?.processedRows ??
                job?.totalRows ??
                0,
            },
            {
              label: t("Total_Import", { fallbackLng: false, fallbackNS: false }),
              value: summary.total ?? job?.totalRows ?? 0,
            },
          ]}
        />
        {hasErrors && (
          <JobErrorPreview errorCount={errorCount} errors={errors} t={t} />
        )}
        {isErrorResult && (
          <Typography color="text.secondary" sx={{ fontSize: 12 }}>
            {isFailed
              ? t("The import job failed. Correct the file and re-upload it.")
              : t("Download the full error report, correct the file, and re-upload it.")}
          </Typography>
        )}
      </Stack>
    );
  };

  const renderError = () => (
    <Alert severity="error">
      {importJobStore.lastError || t("Unable to load import status")}
    </Alert>
  );

  const renderContent = () => {
    if (importJobStore.isCheckingActive) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={30} />
        </Box>
      );
    }
    if (importJobStore.modalView === "history") {
      return <HistoryList t={t} onReupload={handleReupload} />;
    }

    switch (importJobStore.modalState) {
      case "validating":
        return renderValidating();
      case "validation-error":
        return renderValidationError();
      case "locked":
        return renderLocked();
      case "processing":
        return renderProcessing();
      case "success":
      case "completed-with-errors":
      case "failed":
        return renderResult();
      case "error":
        return renderError();
      default:
        return renderIdle();
    }
  };

  const resultHasErrors = importJobStore.selectedJob?.errorRows > 0;
  const resultFailed = importJobStore.selectedJob?.status === "failed";
  const canReupload =
    importJobStore.modalView !== "history" &&
    (importJobStore.modalState === "validation-error" ||
      resultHasErrors ||
      resultFailed ||
      (importJobStore.modalState === "error" && Boolean(file)));
  const canExport =
    !importJobStore.activeJob &&
    !importJobStore.isValidating &&
    !importJobStore.isCheckingActive;
  const validationCloseSx = importJobStore.isValidating
    ? {
        color: "#b8bec8",
        cursor: "not-allowed",
        "&:hover": { backgroundColor: "transparent" },
      }
    : undefined;

  if (!open) return null;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleDialogClose}
      PaperProps={{ sx: { borderRadius: "8px", minHeight: 360 } }}
    >
      <DialogTitle sx={{ px: 2.5, py: 1.75 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ModulePill context={context} />
          <Typography component="span" sx={{ flex: 1, fontSize: 15, fontWeight: 700 }}>
            {t("Import and export")}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            sx={{ height: 32 }}
          >
            <Tooltip title={t("View import history")}>
              <IconButton
                disabled={importJobStore.isValidating}
                size="small"
                sx={{ height: 32, width: 32 }}
                onClick={() => importJobStore.showHistory()}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("Close")}>
              <IconButton
                size="small"
                sx={{ height: 32, width: 32, ...validationCloseSx }}
                onClick={closeModal}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </DialogTitle>
      <Divider />
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
          minHeight: 60,
          px: 2.5,
          py: 1,
        }}
      >
        {isDataEntry && (
          <ToggleButtonGroup
            exclusive size="small" value={isSummary ? "summary" : "split"}
            aria-label={t("Excel format")}
            disabled={importJobStore.isExporting}
            onChange={(_event, value) => value && setIsSummary(value === "summary")}
            sx={{
              mr: "auto",
              "& .MuiToggleButton-root.Mui-selected": {
                color: "#1a56a8",
                backgroundColor: "#eaf2ff",
                "&:hover": { backgroundColor: "#dce9fc" },
              },
            }}
          >
            <ToggleButton value="split">{deText("split")}</ToggleButton>
            <ToggleButton value="summary">{deText("summary")}</ToggleButton>
          </ToggleButtonGroup>
        )}
        {context.module === IMPORT_MODULES.EF && (
          <ToggleButtonGroup
            exclusive
            size="small"
            sx={{ height: 36 }}
            value={exportAll ? "all" : "category"}
            onChange={(_event, value) => value && setExportAll(value === "all")}
          >
            <ToggleButton
              sx={{ fontSize: 12, height: 36, lineHeight: 1, minWidth: 44, px: 1.25, py: 0 }}
              value="all"
            >
              {t("All")}
            </ToggleButton>
            <ToggleButton
              sx={{ fontSize: 12, height: 36, lineHeight: 1, minWidth: 44, px: 1.25, py: 0 }}
              value="category"
            >
              {t("Category")}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        <Button
          disabled={!canExport || missingCategory || importJobStore.isExporting}
          size="small"
          sx={{ height: 36, px: 1.5 }}
          startIcon={
            importJobStore.isExporting ? (
              <CircularProgress size={14} />
            ) : (
              <DownloadIcon />
            )
          }
          variant="outlined"
          onClick={handleExport}
        >
          {t("Export")}
        </Button>
      </Box>
      {isDataEntry && (
        <Stack spacing={1} sx={{ px: 2.5, pb: 1.5 }}>
          {missingCategory && <Alert severity="info">{deText("selectCategory")}</Alert>}
          {!isSummary && (
            <Typography sx={{ fontSize: 12 }} color="text.secondary">
              {t("When Actions is Modify, clearing a month cell deletes that month's data. When Actions is Delete, only the data in that row is deleted.")}
            </Typography>
          )}
          <Typography sx={{ fontSize: 12 }} color="text.secondary">
            {isSummary
              ? t("Import results are counted by Excel row. Each month is a separate row.")
              : t("Import results are counted by Excel row. A row on the YearMonth sheet counts as one row, even if it has values for all 12 months.")}
          </Typography>
        </Stack>
      )}
      <Divider />
      <DialogContent sx={{ px: 2.5, py: 2.25 }}>{renderContent()}</DialogContent>
      <Divider />
      <DialogActions sx={{ minHeight: 64, px: 2.5, py: 1.25 }}>
        {importJobStore.modalView !== "import" && (
          <Button onClick={() => importJobStore.showImport()}>
            {t("Back to import")}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {importJobStore.modalState === "validation-error" && (
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => importJobStore.downloadReport(importJobStore.validation)}
          >
            {t("Download error report")}
          </Button>
        )}
        {resultHasErrors && importJobStore.modalView !== "history" && (
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => importJobStore.downloadReport(importJobStore.selectedJob)}
          >
            {t("Download report")}
          </Button>
        )}
        {canReupload && (
          <Button startIcon={<RefreshIcon />} onClick={resetForUpload}>
            {t("Re-upload")}
          </Button>
        )}
        {importJobStore.modalState === "idle" &&
          importJobStore.modalView === "import" && (
            <Button
              disabled={!file}
              variant="contained"
              onClick={() => importJobStore.validateAndSubmit(file, context)}
            >
              {t("Import")}
            </Button>
          )}
        <Button
          color="inherit"
          sx={validationCloseSx}
          onClick={closeModal}
        >
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const getModuleMetaLabel = (module) => {
  const labels = {
    [IMPORT_MODULES.EA]: "Emission Activity",
    [IMPORT_MODULES.DE]: "Data Entry",
    [IMPORT_MODULES.EF]: "Emission Factor",
  };
  return labels[module] || module;
};

export const ImportJobModal = observer(() => {
  if (commonStore.showExcelImportExportModal && commonStore.excelImportType === "organization_list") {
    return <OrganizationListExcelModal showHistory={Boolean(importJobStore.pendingOpenItem && !importJobStore.pendingOpenItem.reupload)} onClose={() => {
      importJobStore.pendingOpenItem = null;
      commonStore._handleSetIsShowExcelImportExportModal(false);
    }} />;
  }
  return <WorkflowImportJobModal />;
});

export default ImportJobModal;
